import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight.tsx";
import { IconCode } from "@/assets/icons/geist/IconCode.tsx";
import { IconGrid } from "@/assets/icons/geist/IconGrid.tsx";
import { IconLaptop } from "@/assets/icons/geist/IconLaptop.tsx";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button.tsx";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { Link } from "@/components/flexkit/Link";
import { cn } from "@/components/podkit/lib/cn.ts";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { toast } from "@/components/podkit/toasts/use-toast.tsx";
import { Heading3 } from "@/components/podkit/typography/Headings.tsx";
import { Text } from "@/components/podkit/typography/Text";
import { AllProviders } from "@/components/runners/details/source-control-providers.tsx";
import { SourceControlProvider } from "@/components/runners/details/SourceControlProvider.tsx";
import { DisableLocalRunnersModal } from "@/components/runners/DisableLocalRunnersModal.tsx";
import { Toggle } from "@/components/Toggle.tsx";
import { useDesktop } from "@/hooks/use-desktop";
import { useLocalRunner } from "@/hooks/use-local-runner";
import { useMembership } from "@/hooks/use-membership.ts";
import { TrackLocations } from "@/hooks/use-segment.ts";
import { useListEnvironmentInventory } from "@/queries/environment-queries.ts";
import { useListProjects } from "@/queries/project-queries.ts";
import { type PlainRunner, useLocalConfigurationRunner, useSetLocalRunnersEnabled } from "@/queries/runner-queries";
import { formatError } from "@/utils/errors.ts";
import { pluralizeWithCount } from "@/utils/strings.ts";
import { LocalRunnerStatus } from "frontend-shared/local-runner";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb.ts";
import { type RunnerConfigurationSchema_SCMConfigSchema } from "gitpod-next-api/gitpod/v1/runner_configuration_pb.ts";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type FC, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export const LocalRunnerSettings: FC = () => {
    const { data: localConfigRunner, isPending: isPendingLocalConfigRunner } = useLocalConfigurationRunner();

    const { membership, isPending: isLoadingMembership } = useMembership();
    const ready = !isLoadingMembership && !isPendingLocalConfigRunner;
    const readOnly = !ready || membership?.userRole !== OrganizationRole.ADMIN;

    const { isDesktop } = useDesktop();
    const localRunner = useLocalRunner();

    if (!ready) {
        return <LoadingState />;
    }

    if (!localConfigRunner) {
        return null;
    }

    const localRunnerIsFailed = LocalRunnerStatus.isFailed(localRunner.status);

    return (
        <div data-testid="local-runner-settings" className="flex flex-col gap-6">
            <Heading3>Gitpod Desktop</Heading3>
            {isDesktop && localRunnerIsFailed && (
                <Banner
                    text={
                        <div>
                            There appears to be an issue with Gitpod Desktop.{" "}
                            <Link to={"/local-runner"}>Please check the status</Link>
                        </div>
                    }
                    variant="warning"
                    data-testid="remote-stopped-environment"
                />
            )}

            <LocalRunnerSettingsCard localConfigRunner={localConfigRunner} readOnly={readOnly} />
        </div>
    );
};

type LocalRunnerSettingsCardProps = {
    localConfigRunner: PlainRunner;
    readOnly: boolean;
};

const LocalRunnerSettingsCard = ({ localConfigRunner, readOnly }: LocalRunnerSettingsCardProps) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);

    const schemas = useMemo(() => {
        // Only show PAT providers.
        return AllProviders.filter((p) => p.schema.pat).map((p) => p.schema);
    }, []);

    const { data: projectsData, isPending: isPendingProjects } = useListProjects();
    const numProjects = useMemo(() => {
        return (
            projectsData?.projects.filter((p) => {
                return p.environmentClass?.environmentClass?.case === "localRunner";
            }).length ?? 0
        );
    }, [projectsData]);

    const {
        data: environmentsData,
        hasNextPage: hasNextPageEnvironments,
        isPending: isPendingEnvironments,
    } = useListEnvironmentInventory({
        runnerKind: RunnerKind.LOCAL,
    });
    const numEnvironments = useMemo(() => {
        return environmentsData?.pages.flatMap((page) => page.environments).length ?? 0;
    }, [environmentsData]);

    return (
        <div className="flex flex-col gap-4">
            <div className="rounded-xl border border-border-base">
                <div className="flex flex-col justify-between gap-4 p-6 lg:flex-row lg:items-center lg:gap-8">
                    <div className="flex min-w-0 items-center gap-4">
                        <IconLaptop size="xl" className="flex-shrink-0" />
                        <div className="flex min-w-0 flex-col gap-1">
                            <Text className="font-semibold">Allow local environments using Gitpod Desktop</Text>
                            <Text className="text-sm text-content-secondary">
                                {readOnly
                                    ? "Organization admins can change these settings"
                                    : "This will affect all members using Gitpod Desktop on this organization"}
                            </Text>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 lg:flex-nowrap">
                        <Text className="whitespace-nowrap text-sm font-bold text-content-secondary">Used by</Text>
                        <div className="flex flex-wrap items-center gap-4 sm:flex-nowrap">
                            <Button
                                LeadingIcon={IconGrid}
                                size="sm"
                                variant="secondary"
                                disabled={numProjects === 0}
                                loading={isPendingProjects}
                                onClick={() => navigate("/projects?environmentClass=local")}
                                className="w-full sm:w-auto"
                                data-testid="local-runner-settings-projects-button"
                            >
                                {pluralizeWithCount(numProjects, false, "Project")}
                            </Button>
                            <Button
                                LeadingIcon={IconCode}
                                size="sm"
                                variant="secondary"
                                disabled={numEnvironments === 0}
                                loading={isPendingEnvironments}
                                onClick={() => navigate("/settings/environments?runner=local")}
                                className="w-full sm:w-auto"
                                data-testid="local-runner-settings-environments-button"
                            >
                                {pluralizeWithCount(numEnvironments, hasNextPageEnvironments, "Environment")}
                            </Button>
                            <LocalRunnersToggleButton localConfigRunner={localConfigRunner} disabled={readOnly} />
                        </div>
                    </div>
                </div>
                <RepositoryAccessSettings
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                    schemas={schemas}
                    localConfigRunner={localConfigRunner}
                />
            </div>
        </div>
    );
};

type RepositoryAccessSettingsProps = {
    isExpanded: boolean;
    setIsExpanded: (expanded: boolean) => void;
    schemas: RunnerConfigurationSchema_SCMConfigSchema[];
    localConfigRunner: PlainRunner;
};

const RepositoryAccessSettings: FC<RepositoryAccessSettingsProps> = ({
    isExpanded,
    setIsExpanded,
    schemas,
    localConfigRunner,
}) => {
    return (
        <div className="border-t border-border-base">
            <Collapsible open={isExpanded} className="flex flex-col gap-1">
                <CollapsibleTrigger asChild>
                    <Button
                        variant="ghost"
                        size="lg"
                        className="flex h-16 w-full items-center justify-between border-0 p-4 transition-transform hover:cursor-pointer hover:bg-transparent"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        <Text className="text-lg font-normal">Gitpod Desktop repository access settings</Text>
                        <IconChevronRight className={cn("mr-4", isExpanded && "rotate-90")} size={"base"} />
                    </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="p-4 pt-0">
                        <SourceControlProvider
                            runner={localConfigRunner}
                            hasStack={true}
                            withDescription={false}
                            schemas={schemas}
                            Container={({ children }) => <div className="flex flex-col gap-4">{children}</div>}
                            data-track-location={(modalState) =>
                                modalState.type == "add-provider"
                                    ? TrackLocations.AddLocalSCMProviderModal
                                    : modalState.type == "remove-provider"
                                      ? TrackLocations.DeleteLocalSCMProviderModal
                                      : TrackLocations.EditLocalSCMProviderModal
                            }
                        />
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};
const LocalRunnersToggleButton: FC<{ localConfigRunner: PlainRunner; disabled: boolean }> = ({
    localConfigRunner,
    disabled,
}) => {
    const setLocalRunnersEnabled = useSetLocalRunnersEnabled(localConfigRunner);
    const enabled = localConfigRunner.spec?.desiredPhase === RunnerPhase.ACTIVE;

    const [showDisableLocalRunnersModal, setShowDisableLocalRunnersModal] = useState(false);

    const handleToggle = useCallback(async () => {
        if (enabled) {
            setShowDisableLocalRunnersModal(true);
        } else {
            try {
                await setLocalRunnersEnabled.mutateAsync({ enabled: true });
                toast({
                    title: "Enabled local environments",
                });
            } catch (error) {
                toast({
                    title: "Failed to enable local environments",
                    description: formatError(error),
                });
            }
        }
    }, [enabled, setLocalRunnersEnabled]);

    return (
        <>
            <Toggle
                state={setLocalRunnersEnabled.isPending ? "pending" : enabled ? "checked" : "unchecked"}
                disabled={disabled}
                onToggle={handleToggle}
                fixedWidth={true}
                id="local-runners-toggle"
            />
            {showDisableLocalRunnersModal && (
                <DisableLocalRunnersModal
                    localConfigRunner={localConfigRunner}
                    onClose={() => setShowDisableLocalRunnersModal(false)}
                />
            )}
        </>
    );
};
